// UserCell — name renderer for embedded UserSummary fields.
// Use anywhere a list row / detail card needs to show "who" (uploader,
// creator, head, actor, etc.) — keeps display rules consistent across
// Media, Folder, Knowledge, Workflow, Connector, Audit, …
import { cn } from "@/utils";
import type { UserSummary } from "@/types/api.types";

export type UserCellSize = "xs" | "sm" | "md" | "lg";

export interface UserCellProps {
  user?: UserSummary | null;
  /** Drives text size only; kept for callsite compat across list/detail rows. */
  size?: UserCellSize;
  /** Show email line under the name. Defaults to false. */
  showEmail?: boolean;
  /** Optional trailing slot — e.g. relative timestamp. */
  trailing?: React.ReactNode;
  /** Text rendered when user is null/undefined. Default em-dash. */
  fallbackText?: string;
  className?: string;
  /**
   * "default" → use design-token text colors (foreground / muted-foreground).
   * "inherit" → name + email inherit from the parent's text color so the
   *   cell reads correctly inside themed surfaces (gradient hero cards,
   *   colored toasts, etc.) where the global foreground token clashes.
   */
  tone?: "default" | "inherit";
}

function displayName(user: UserSummary): string {
  const first = (user.firstName ?? "").trim();
  const last = (user.lastName ?? "").trim();
  const full = `${first} ${last}`.trim();
  return full || user.username || user.email || "";
}

export function UserCell({
  user,
  size = "xs",
  showEmail = false,
  trailing,
  fallbackText = "—",
  className,
  tone = "default",
}: UserCellProps) {
  const nameColor =
    tone === "inherit" ? "text-current" : "text-[var(--foreground)]";
  const mutedColor =
    tone === "inherit"
      ? "text-current opacity-75"
      : "text-[var(--muted-foreground)]";

  if (!user) {
    return (
      <span className={cn(mutedColor, className)}>
        {fallbackText}
      </span>
    );
  }

  const name = displayName(user);
  // Keep xs/sm rows compact so they read as one visual unit alongside chips
  // and timestamps; md/lg get the standard text-sm.
  const nameTextClass =
    size === "xs" || size === "sm" ? "text-xs" : "text-sm";

  return (
    <div className={cn("flex min-w-0 flex-col leading-tight", className)}>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className={cn("truncate", nameColor, nameTextClass)} title={name}>
          {name}
        </span>
        {trailing && (
          <span className={cn("shrink-0 text-[11px]", mutedColor)}>
            {trailing}
          </span>
        )}
      </span>
      {showEmail && (user.username || user.email) && (user.email ?? user.username) !== name && (
        <span className={cn("truncate text-[11px]", mutedColor)} title={user.email ?? user.username ?? ""}>
          {user.email ?? user.username}
        </span>
      )}
    </div>
  );
}

export default UserCell;
